import { Box, Stack } from "@mui/material";
import { useTheme } from '@mui/material/styles';
import { useKeycloak } from "@react-keycloak/web";
import { useAtom, useAtomValue } from "jotai";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { ChatView } from ".";
import {
    baseAtom,
    checkRole,
    getChannels,
    getUIStateIsOptionSelected,
    loadChannelHistory,
    postMessageToChannel,
    setUIStateIsOptionSelected,
} from "../../stores/jotai/base";
import { discoverySessionAtom } from "../../stores/jotai/discovery-session";
import { protectedApiCall } from "../../api/network";
import { scoringSelectedPeriodAtom } from "../../stores/jotai/scoring/selectedPeriodIndex";
import { isInPresentationMode } from "../../util";

export default function MaterialChatContainer(props) {
    const [base, setBase] = useAtom(baseAtom);
    const { keycloak } = useKeycloak();
    const discoverySession = useAtomValue(discoverySessionAtom);
    const selectedPeriodIndex = useAtomValue(scoringSelectedPeriodAtom);
    const isInstructor = checkRole(base, "isInstructor");
    const location = useLocation()
    const theme = useTheme();
    const isInPresentation = isInPresentationMode(location.pathname);

    const instructorChannels = useMemo(() => {
        return [
            {
                key: `${base?.team?.groupID}P${selectedPeriodIndex}`,
                value: "Presentation Chat",
            },
        ];
    }, [selectedPeriodIndex]);

    const sendMessage = (message: string, channelKey: string) => {
        // we already know the channel, and it can only come from
        // our signed in user
        const messageID = crypto.randomUUID();

        postMessageToChannel(
            discoverySession,
            setBase,
            channelKey,
            {
                id: messageID,
                isOptimistic: true,
                message,
                created: new Date().toUTCString(),
                modified: null,
                userID: base?.user?.userID,
                name: base?.user?.name,
                groupID: base?.team?.groupID,
                channelID: channelKey,
                photoURL: base?.user?.photoURL,
                origination: "discovery-v3",
            },
            () => {
                protectedApiCall(
                    discoverySession,
                    "seen",
                    {
                        type: "chat",
                        targets: [messageID],
                    },
                    {},
                    "put"
                );
            }
        );
    };

    const generalChannels = useMemo(() => {
        return getChannels(base);
    }, [base]);

    const chatOpen = getUIStateIsOptionSelected(base, "chat");
    
    // this memoization pattern refreshes the ChatView component when the underlying channel set changes
    // otherwise the state is within this context
    const memoChatContainer = () => {
        return (
            <Stack>
                {isInPresentation && isInstructor && (
                    <Box sx={{ height: "50%" }}>
                        <ChatView
                            baseAtomBridge={base}
                            handleChatClose={
                                isInPresentation
                                    ? undefined
                                    : () =>
                                        setUIStateIsOptionSelected(
                                            setBase,
                                            base,
                                            "chat",
                                            false
                                        )
                            }
                            loadChannelChat={(channelKey, onSuccess?, onFailure?) => {
                                loadChannelHistory(
                                    setBase,
                                    channelKey,
                                    keycloak.token,
                                    onSuccess,
                                    onFailure
                                );
                            }}
                            publishMessage={sendMessage}
                            pubState={{
                                splitViewActive: true,
                            }}
                            appState={base?.app}
                            presentationGroup={undefined}
                            presentationWeek={undefined}
                            org={base?.org}
                            {...props}
                            inPresentation={true}
                            channels={instructorChannels}
                            canSplit={true}
                            isTop={true}
                            isInstructorChat={true}
                            theme = {theme}
                        />
                    </Box>
                )}
                <Box
                    sx={{ flexGrow: 1, height: isInPresentation ? "50%" : "100%" }}
                >
                    <ChatView
                        baseAtomBridge={base}
                        handleChatClose={
                            isInPresentation
                                ? undefined
                                : () =>
                                    setUIStateIsOptionSelected(setBase, base, "chat", false)
                        }
                        loadChannelChat={(channelKey, onSuccess?, onFailure?) => {
                            loadChannelHistory(
                                setBase,
                                channelKey,
                                keycloak.token,
                                onSuccess,
                                onFailure
                            );
                        }}
                        publishMessage={sendMessage}
                        pubState={{
                            splitViewActive: false,
                        }}
                        appState={base?.app}
                        presentationGroup={undefined}
                        presentationWeek={undefined}
                        org={base?.org}
                        {...props}
                        inPresentation={false}
                        channels={generalChannels}
                        canSplit
                        isTop
                        theme={theme}
                    />
                </Box>
            </Stack>
        );
    };

    if (chatOpen === false) {
        return <span></span>;
    }

    return memoChatContainer();
}
